/**
 * SubtitleOverlay Component
 *
 * Displays real-time subtitles over video content with customizable styling.
 */

import { useTranslation } from 'react-i18next';
import type { SubtitleData } from '../hooks/useSubtitles';

export interface SubtitleOverlayProps {
  /** Current subtitle to display */
  subtitle: SubtitleData | null;
  /** Subtitle history for multi-line display */
  history?: SubtitleData[];
  /** Whether the overlay is visible */
  visible?: boolean;
  /** Show translated text instead of original */
  showTranslation?: boolean;
  /** Show both original and translated text */
  showBoth?: boolean;
  /** Position of the overlay */
  position?: 'top' | 'bottom';
  /** Font size preset */
  fontSize?: 'small' | 'medium' | 'large';
  /** Show visual indicator for interim (non-final) subtitles */
  showInterimIndicator?: boolean;
  /** Show subtitle history */
  showHistory?: boolean;
  /** Number of history lines to show */
  historyLines?: number;
  /** Error message to display */
  error?: string | null;
  /** Whether connection is in progress */
  isConnecting?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const FONT_SIZE_CLASSES = {
  small: 'text-base',
  medium: 'text-lg',
  large: 'text-2xl',
};

export default function SubtitleOverlay({
  subtitle,
  history = [],
  visible = true,
  showTranslation = false,
  showBoth = false,
  position = 'bottom',
  fontSize = 'medium',
  showInterimIndicator = false,
  showHistory = false,
  historyLines = 3,
  error,
  isConnecting = false,
  className = '',
}: SubtitleOverlayProps) {
  const { t } = useTranslation('watch');
  // Don't render if not visible
  if (!visible) {
    return null;
  }

  // Show connecting state
  if (isConnecting) {
    return (
      <div
        data-testid="subtitle-overlay"
        role="status"
        aria-live="polite"
        className={`absolute ${position === 'top' ? 'top-4' : 'bottom-4'} left-0 right-0 px-4 ${className}`}
      >
        <div className="mx-auto max-w-4xl bg-black/75 rounded-lg px-4 py-2 text-center">
          <span className="text-white text-sm">{t('subtitleStatus.connecting', '接続中...')}</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        data-testid="subtitle-overlay"
        role="status"
        aria-live="polite"
        className={`absolute ${position === 'top' ? 'top-4' : 'bottom-4'} left-0 right-0 px-4 ${className}`}
      >
        <div
          data-testid="subtitle-error"
          className="mx-auto max-w-4xl bg-red-900/75 rounded-lg px-4 py-2 text-center"
        >
          <span className="text-red-200 text-sm">{error}</span>
        </div>
      </div>
    );
  }

  // Don't render if no subtitle
  if (!subtitle) {
    return null;
  }

  // Determine which text to display
  const getDisplayText = (sub: SubtitleData): string => {
    if (showTranslation && sub.translated_text) {
      return sub.translated_text;
    }
    return sub.text;
  };

  // Get history to display (limited to historyLines)
  // Exclude current subtitle from history to avoid duplication
  const displayHistory = showHistory
    ? history
        .filter((item) => !subtitle || item.subtitle_id !== subtitle.subtitle_id)
        .slice(-historyLines)
    : [];

  const fontSizeClass = FONT_SIZE_CLASSES[fontSize];
  const isInterim = showInterimIndicator && !subtitle.is_final;

  return (
    <div
      data-testid="subtitle-overlay"
      role="status"
      aria-live="polite"
      className={`absolute ${position === 'top' ? 'top-4' : 'bottom-4'} left-0 right-0 px-4 ${className}`}
    >
      <div className="mx-auto max-w-4xl space-y-1">
        {/* History lines */}
        {displayHistory.map((historyItem) => (
          <div
            key={historyItem.subtitle_id}
            className="bg-black/50 rounded-lg px-4 py-1 text-center"
          >
            <span className="text-white/70 text-base">
              {getDisplayText(historyItem)}
            </span>
          </div>
        ))}

        {/* Current subtitle */}
        <div className="bg-black/75 rounded-lg px-4 py-2 text-center">
          {/* Original text (when showing both or not using translation) */}
          {showBoth && subtitle.translated_text && (
            <div className="text-white/70 text-sm mb-1">
              {subtitle.text}
            </div>
          )}

          {/* Main subtitle text */}
          <span
            data-testid="subtitle-text"
            className={`text-white ${fontSizeClass} ${isInterim ? 'italic opacity-70' : ''}`}
          >
            {getDisplayText(subtitle)}
          </span>

          {/* Translated text below original when showBoth is true */}
          {showBoth && subtitle.translated_text && !showTranslation && (
            <div className="text-white text-lg mt-1">
              {subtitle.translated_text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
